import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Api } from "../../api.config";
import { AlertType, useAlertEmit } from "../../common/alert/Alert";
import { useCompany } from "../../company/useCompany";
import { QueryKeys } from "../../LinkGen";

export const useAIVisibility = () => {
	const { id: companyId } = useCompany();

	return useSuspenseQuery({
		queryKey: QueryKeys.Seo.AIVisibility(companyId),
		queryFn: async () => (await Api.GetLatestAIVisibility()).data,
	}).data;
};

export const useSeoMetrics = (months = 6) => {
	const { id: companyId } = useCompany();

	return useSuspenseQuery({
		queryKey: QueryKeys.Seo.Metrics(companyId),
		queryFn: async () => (await Api.GetSeoMetrics(months)).data,
	}).data;
};

const defaultRunErrorMessage =
	"Failed to run AI visibility. Please try again later.";

export const useAIVisibilityActions = () => {
	const queryClient = useQueryClient();
	const emit = useAlertEmit();
	const { id: companyId } = useCompany();

	const run = useMutation({
		mutationFn: async () => (await Api.RunAIVisibility()).data,
		onSuccess: async () => {
			await Promise.all([
				queryClient.refetchQueries({
					queryKey: QueryKeys.Seo.AIVisibility(companyId),
				}),
				queryClient.refetchQueries({
					queryKey: QueryKeys.Seo.Metrics(companyId),
				}),
			]);

			emit({
				type: AlertType.SUCCESS,
				content: "AI visibility has finished running.",
			});
		},
		onError: (e: unknown) => {
			if (isAxiosError(e)) {
				emit({
					type: AlertType.ERROR,
					content: e.response?.data?.message || defaultRunErrorMessage,
				});

				return;
			}

			emit({
				type: AlertType.ERROR,
				content: defaultRunErrorMessage,
			});
		},
	});

	return { run };
};
